import {
    AddBatchBody,
    CreateProductBody,
    Product,
    ProductFilterParams,
    UpdateProductBody,
} from '@core/models/product.model';
import { AxiosResponse } from 'axios';
import API from 'services/api';
import { fetchWithRetry } from 'services/fetchWithRetry';

class ProductService {
    private readonly getProductsURL = `/products/get_products`;
    private readonly getProductByIdURL = `/products/product`;
    private readonly createProductURL = `/products/create_product`;
    private readonly updateProductURL = `/products/update_product`;
    private readonly deleteProductURL = `/products/delete_product`;
    private readonly addBatchURL = `/batches/add-batch`;
    private readonly getBestSellerProductsURL = `/products/best/seller/products`;
    private readonly updateBestSellerURL = `/products/update_best_seller`;
    private readonly exportProductsURL = `/export/products`;
    private readonly getProductLifeCycleURL = `/admin/product-lifecycle/product/`;
    private readonly getInventoryHistorybyProductURL = `/admin/inventory/product/`;

    // ---------------------------------------------------------------

    getProducts(params?: ProductFilterParams) {
        return fetchWithRetry(API.get(this.getProductsURL, { params }));
    }

    getProductById(id: number) {
        return fetchWithRetry(API.get(`${this.getProductByIdURL}/${id}`));
    }

    createProduct(body: CreateProductBody) {
        const formData = new FormData();

        Object.keys(body).forEach((key) => {
            const value = body[key as keyof CreateProductBody];
            if (key === 'photoFile' && value instanceof File) {
                formData.append(key, value);
            } else if (value !== undefined && value !== null && value !== '') {
                formData.append(key, String(value));
            }
        });

        return fetchWithRetry(
            API.post(this.createProductURL, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
        );
    }

    updateProduct(body: UpdateProductBody) {
        const formData = new FormData();

        Object.keys(body).forEach((key) => {
            const value = body[key as keyof UpdateProductBody];
            if (key === 'photoFile' && value instanceof File) {
                formData.append(key, value);
            } else if (value !== undefined && value !== null && value !== '') {
                formData.append(key, String(value));
            }
        });

        return fetchWithRetry(
            API.patch(this.updateProductURL, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
        );
    }

    deleteProduct(id: number) {
        return fetchWithRetry(API.delete(`${this.deleteProductURL}/${id}`));
    }

    addBatch(body: AddBatchBody) {
        return fetchWithRetry(API.post(this.addBatchURL, body));
    }

    getBestSellerProducts(params?: { pageNumber?: number; productSize?: number; sizeBestSeller?: number }) {
        return fetchWithRetry(API.get(this.getBestSellerProductsURL, { params }));
    }

    updateBestSeller(itemName: string, isShow: boolean) {
        return fetchWithRetry(API.patch(`${this.updateBestSellerURL}/${itemName}?isShow=${isShow}`));
    }

    exportProducts(body?: { subCategories?: string; category?: string; dosages?: string; inStockOnly?: boolean }) {
        return fetchWithRetry(
            API.post(this.exportProductsURL, {
                subCategories: body?.subCategories || '',
                category: body?.category || '',
                dosages: body?.dosages || '',
                inStockOnly: body?.inStockOnly ?? false,
            })
        );
    }

    getInventoryHistorybyProduct(productId: number) {
        return fetchWithRetry(API.get(`${this.getInventoryHistorybyProductURL}${productId}/history`));
    }

    getProductLifeCycle(productId: number) {
        return fetchWithRetry(API.get(`${this.getProductLifeCycleURL}${productId}`));
    }
}

export default new ProductService();
